import type { DataTransferObject } from '@svenskaspelab/nova-payment-sport';
import { createLogger } from '@svs/nova-core/log';
import type { trackEcommerce, trackEvent } from '@svs/nova-core/nextjs/analytics';
import type { Role } from '@svs/nova-core/nextjs/auth';
import type { DialogConfig } from '@svs/nova-foundation/nextjs/dialog';

import { type CouponId, type CouponIndex, getCouponIndex } from '../../../../schema/coupon/entry.js';
import type { Commission } from '../../../../schema/input/entry.js';
import type { ActivatedDrawId } from '../../../../schema/marketplace/entry.js';
import { getInstanceClient } from '../../../di/entry.browser.js';
import { CouponEntity, NotInitializedError, PoolbettingError } from '../../../domain/entry.js';
import type { ActionResult } from '../../../lib/entry.browser.js';

const logger = createLogger('poolbetting.place-coupon-action');

type PlaceCouponArgs = {
  couponId: CouponId;
  showDialog: (props: DialogConfig, meta?: string[]) => Promise<void>;
};

type PlaceCouponReturn = ActionResult<{
  transactionIds: DataTransferObject['transactionId'][];
  paidActivatedDrawIds: ActivatedDrawId[];
}>;

type PaymentContextArgs = {
  role?: Role;
  commissionPercent?: Commission;
  activatedDrawId?: ActivatedDrawId;
  paymentUrls?: Record<string, string | undefined>;
  marketplaceWallets?: Record<ActivatedDrawId, { balance: number; soldShares: number; sharePrice: number }>;
  trackTransaction: typeof trackEcommerce;
  trackEventFunction: typeof trackEvent;
};

/**
 * Places valid coupons via given couponId.
 *
 * @param root0 Place coupon args
 * @param root0.couponId Unique coupon id to place bet(s) on
 * @param root0.showDialog Dialog function to show confirmation dialog
 * @param paymentContext Payment context args such as user role, optional commission percentage, and optional payment URLs
 * @returns status: SUCCESS with data if successful, otherwise status: ERROR with error message
 */
export async function placeCouponWager(
  { couponId, showDialog }: PlaceCouponArgs,
  paymentContext: PaymentContextArgs
): Promise<PlaceCouponReturn> {
  const getCouponsController = getInstanceClient('IGetCouponsController');
  const createWagerController = getInstanceClient('ICreateWagerController');
  const definitionService = getInstanceClient('IDefinitionService');
  const getCouponWalletsController = getInstanceClient('IGetCouponWalletsController');
  const resetCouponController = getInstanceClient('IResetCouponController');
  const updateCouponController = getInstanceClient('IUpdateCouponController');

  const [getCoupons] = getCouponsController({ couponId });
  const wallets = getCouponWalletsController({ couponId });
  try {
    const coupons = getCoupons();
    const [firstCoupon] = coupons;

    if (!firstCoupon) {
      return { status: 'ERROR', error: 'Det finns inget att betala' };
    }

    // For now its only valid to place wagers on same product given the coupon id
    const definition = await definitionService.resolveDefinitionAsync(firstCoupon.product);

    if (!definition) {
      throw new NotInitializedError(`Unable to load definition for product: ${firstCoupon.product}`);
    }

    const validCouponIndices: CouponIndex[] = [];
    for (const [couponIndex, coupon] of coupons.entries()) {
      const isValid = CouponEntity.getStatus(coupon, { definition: definition.model }) === 'VALID';
      if (isValid && getCouponIndex(couponIndex)) {
        validCouponIndices.push(couponIndex);
      }
    }

    const hasInvalidCouponIndices = validCouponIndices.length !== coupons.length;

    const { couponType } = firstCoupon;
    const wagerResponse = await createWagerController({
      couponId,
      couponIndices: validCouponIndices,
      wallets,
      showDialog,
      paymentContext,
      hasInvalidCouponIndices: hasInvalidCouponIndices,
      couponType,
    });

    if (wagerResponse.unpaidItemsCount === 0 && !hasInvalidCouponIndices && validCouponIndices[0] !== undefined) {
      await resetCouponController({ couponId, couponIndex: validCouponIndices[0] });
      await updateCouponController(
        { betAmount: definition.betAmount },
        { couponId, couponIndex: validCouponIndices[0] }
      );
    }

    const numberOfPaidWagers = wagerResponse.paidItemsCount;

    if (numberOfPaidWagers > 0) {
      paymentContext.trackEventFunction({
        category: 'Nya tipsen',
        action: 'Betalning klar',
        optLabel: `Antal kuponger: ${numberOfPaidWagers}`,
      });
    }

    return {
      status: 'SUCCESS',
      data: {
        transactionIds: wagerResponse.transactionIds,
        paidActivatedDrawIds: wagerResponse.paidActivatedDrawIds,
      },
    };
  } catch (error) {
    if (PoolbettingError.isInstance(error)) {
      return error.toActionResult();
    }

    logger.error('Failed to place coupon wager', { error, couponId });

    return {
      status: 'ERROR',
      error: 'Något gick fel vid betalningen',
      reason: error instanceof Error && typeof error.cause === 'string' ? error.cause : undefined,
    };
  }
}
